import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Flex, Image, Text } from '@valwin/facto/components';
import {
  EarthIcon,
  MessageCircleIcon,
  ShareArrowIcon,
  ThumbsUpIcon,
  UserIcon,
} from '@valwin/facto/icons';

import type { FacebookPage } from '~features/integrations/facebook/types';
import { PhonePreview } from '~features/integrations/ui';

import { useFacebookShareForm } from '../context';

const CHARS_PER_LINE = 35;

type PostMessageProps = {
  message: string;
  shareUrl: string;
};

function PostMessage({ message, shareUrl }: PostMessageProps) {
  const { t } = useTranslation();

  const [isExpanded, setIsExpanded] = useState(false);
  const seeMoreLabel = t('common seeMore');
  const maxLength = CHARS_PER_LINE * 2 - seeMoreLabel.length;
  const displayedMessage = isExpanded
    ? message
    : `${message.slice(0, maxLength)}...`;

  function onClick() {
    setIsExpanded(true);
  }

  return (
    <Box paddingX="2">
      <Text textStyle="xs" whiteSpace="pre-wrap">
        {displayedMessage}
        {!isExpanded && (
          <Box
            as="span"
            color="fg.muted"
            cursor="pointer"
            marginLeft="1"
            onClick={onClick}
          >
            {seeMoreLabel}
          </Box>
        )}
        {isExpanded && shareUrl && (
          <>
            {'\n\n'}
            <Box as="span" color="blue.500">
              {shareUrl}
            </Box>
          </>
        )}
      </Text>
    </Box>
  );
}

type FacebookSharePreviewProps = {
  shareUrl: string;
  pages: FacebookPage[];
};

export function FacebookSharePreview({
  shareUrl,
  pages,
}: FacebookSharePreviewProps) {
  const { watch, setValue } = useFacebookShareForm();

  const message = watch('message');
  const photos = watch('photos');
  const pageId = watch('pageId');

  const currentPage = pages.find((p) => p.pageId === pageId) ?? pages[0];

  return (
    <PhonePreview>
      <Flex direction="column" gap="2" paddingTop="1" paddingBottom="6">
        {currentPage && (
          <Flex align="center" gap="2" paddingX="2">
            <Flex
              alignItems="center"
              justifyContent="center"
              width="8"
              height="8"
              borderRadius="full"
              background="blue.300"
              color="blue.700"
            >
              {currentPage.pageName ? (
                currentPage.pageName[0].toUpperCase()
              ) : (
                <UserIcon size="md" color="blue.700" />
              )}
            </Flex>
            <Flex flexDirection="column">
              <Text textStyle="xs" fontWeight="bold" truncate>
                {currentPage.pageName}
              </Text>
              <Flex alignItems="center" color="fg.muted" gap="1">
                <Text textStyle="xs">1h</Text>
                <Box
                  width="2px"
                  height="2px"
                  borderRadius="full"
                  bg="fg.muted"
                />
                <EarthIcon size="xs" />
              </Flex>
            </Flex>
          </Flex>
        )}

        {message && <PostMessage message={message} shareUrl={shareUrl} />}

        <Box
          width="full"
          aspectRatio={photos?.[0] ? undefined : '1080/1350'}
          background={photos?.[0] ? 'transparent' : 'gray.100'}
          overflow="hidden"
        >
          {photos?.[0] && (
            <Image
              src={photos[0]}
              alt="Preview"
              width="full"
              objectFit="cover"
              onError={() => setValue('photos', [])}
            />
          )}
        </Box>

        <Flex gap="3" paddingX="3" textStyle="xs" color="gray.500">
          <ThumbsUpIcon size="md" />
          <MessageCircleIcon size="md" />
          <ShareArrowIcon size="md" />
        </Flex>
      </Flex>
    </PhonePreview>
  );
}
